import { AfterViewInit, Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { AbstractControl, FormControl, FormGroup } from '@angular/forms';
import {
  AvailableLanguages,
  isOtherLegalBasis,
  isSchengenCountryIssuedDocumentLegalBasis,
  isVisaLegalBasis,
  legalBasisOfStayList,
  shouldShowEntryDocumentFields,
} from '@constants';
import { DeviceInfoService } from '@core/services/device-info.service';
import { DeviceType, VISA_TYPE } from '@interfaces';
import { TranslateService } from '@ngx-translate/core';
import moment from 'moment-timezone';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-temp-premit-step4',
  templateUrl: './temp-premit-step4.component.html',
  styleUrls: ['./temp-premit-step4.component.scss'],
  standalone: false,
})
export class TempPremitStep4Component implements OnInit, AfterViewInit, OnDestroy {
  private readonly translateService = inject(TranslateService);
  private readonly deviceInfo = inject(DeviceInfoService);

  private destroy$: ReplaySubject<boolean> = new ReplaySubject(1);

  @Input() formGroup: FormGroup;
  @Input() mode: 'foreigner' | 'employee';
  @Output() firstInputShiftTabPressed: EventEmitter<void> = new EventEmitter<void>();
  @Output() lastFieldTabPressed: EventEmitter<void> = new EventEmitter<void>();

  public visitsAmount: number;
  public isVisitsSorting: boolean = false;
  public isVisitsLoading: boolean = false;
  public sortClick$ = new Subject<void>();

  public controls: { [key: string]: FormControl } = {};
  public currentLang: AvailableLanguages;
  public currentDate = moment();
  public selectsValues: { [key: string]: { label: string; value: string }[] };

  public minValidDate = moment().subtract(125, 'years');
  public maxValidDate = moment().subtract(1, 'day');
  public maxValidFutureDate = moment().subtract(1, 'day').add(30, 'years');
  public maxValidDocumentExpiryDate = moment().add(15, 'years');
  public documentIssuePickerStartDate = moment().subtract(10, 'years').startOf('year');
  public documentExpiryPickerStartDate = moment().add(1, 'year').startOf('year');
  public useMobileView = false;
  public visaTypes = Object.values(VISA_TYPE);
  public showOtherLegalBasisComment = false;
  public showStayVisaFields = false;
  public showEntitlingDocumentFields = false;
  public showEntryDocumentFields = false;

  protected readonly legalBasisOfStayList = legalBasisOfStayList;

  ngOnInit(): void {
    this.useMobileView = this.deviceInfo.getDeviceType() !== DeviceType.DESKTOP;
    const controls: any = {};
    this.currentLang = this.translateService.currentLang as AvailableLanguages;
    this.selectsValues = this.translateService.translations[this.currentLang].SELECTS_VALUES;

    this.translateService.onLangChange.pipe(takeUntil(this.destroy$)).subscribe(({ lang: langKey }) => {
      this.currentLang = langKey as AvailableLanguages;
      this.selectsValues = this.translateService.translations[this.currentLang].SELECTS_VALUES;
    });

    Object.keys(this.formGroup.controls).forEach(fieldName => {
      controls[fieldName] = this.formGroup.controls[fieldName] as FormControl;
    });
    this.controls = controls;

    this.updateLegalBasisDependentFields(controls.legalBasisOfStay?.value);
    controls.legalBasisOfStay?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe((value: unknown) => {
      this.updateLegalBasisDependentFields(value);
    });

    const lastTravelsOutsidePolandValue = controls.lastTravelsOutsidePoland?.value;
    this.visitsAmount =
      typeof lastTravelsOutsidePolandValue === 'string' && lastTravelsOutsidePolandValue.length
        ? lastTravelsOutsidePolandValue.split(',').length
        : 0;
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.hasAnyFieldAnyValue(this.formGroup.controls)) {
        this.formGroup.markAllAsTouched();
      }
      this.formGroup.controls.currentlyLivingInPoland.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
        this.currentlyLivingInPolandAutoFilling(value);
      });

      this.formGroup.controls.lastTravelsOutsidePoland.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
        if (!value) {
          this.visitsAmount = 0;
          return;
        }

        this.visitsAmount = value.split(',').length;
      });
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.complete();
  }

  public firstInputKeydown($event: KeyboardEvent): void {
    if (!$event.shiftKey) {
      return;
    }

    if ($event.key?.toLowerCase() !== 'tab') {
      return;
    }

    this.firstInputShiftTabPressed.emit();
  }

  public emitLastFieldTabPressed($event: Event): void {
    $event.preventDefault();
    $event.stopPropagation();

    this.lastFieldTabPressed.emit();
  }

  public removeLastEntryDate($event: any): void {
    $event.stopPropagation();
    $event.preventDefault();
    this.controls.lastEntryDate.setValue(null);
  }

  private hasAnyFieldAnyValue(controls: { [key: string]: AbstractControl }): boolean {
    return Object.keys(controls).some(controlName => {
      const val = controls[controlName].value;

      if (Array.isArray(val) && val.length > 0) {
        return true;
      }

      return val !== '' && val !== undefined && val !== null;
    });
  }

  public currentlyLivingInPolandAutoFilling(currentlyLivingInPolandValue: boolean): void {
    const previousVisitsArr = this.formGroup.controls.previousVisitsArr;
    const noPreviousVisits = this.formGroup.controls.noPreviousVisits;

    if (currentlyLivingInPolandValue === true) {
      noPreviousVisits.setValue(false);
      return;
    }
    if (
      currentlyLivingInPolandValue === false &&
      previousVisitsArr?.value?.length === 1 &&
      previousVisitsArr?.value[0]?.leaveDate === 'OBECNIE'
    ) {
      this.formGroup.controls.noPreviousVisits.setValue(true);
      previousVisitsArr.setValue(null);
      return;
    }
  }

  public onVisitsSortingChange(sorting: boolean): void {
    this.isVisitsSorting = sorting;
  }

  public onVisitsLoadingChange(loading: boolean): void {
    this.isVisitsLoading = loading;
  }

  private updateLegalBasisDependentFields(value: unknown): void {
    this.showOtherLegalBasisComment = isOtherLegalBasis(value);
    this.showStayVisaFields = isVisaLegalBasis(value);
    this.showEntitlingDocumentFields = isSchengenCountryIssuedDocumentLegalBasis(value);
    this.showEntryDocumentFields = shouldShowEntryDocumentFields(value);
  }
}
